import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT, type MessageKey } from '@/shared/i18n';
import { Icon } from '@/shared/ui/icon';
import { useNotifications } from '../hooks/useNotifications';
import type {
  AppNotification,
  NotificationFilter,
} from '../models/notification.types';

const TABS: { key: NotificationFilter; labelKey: MessageKey }[] = [
  { key: 'all', labelKey: 'notifications.tab.all' },
  { key: 'unread', labelKey: 'notifications.tab.unread' },
  { key: 'read', labelKey: 'notifications.tab.read' },
];

/** Humanise a notification `type` (e.g. `ai-assistant` → `Ai assistant`) for the "From" line. */
function sourceLabel(type: string): string {
  const spaced = type.replace(/[-_]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : type;
}

/** Relative time ("5 minutes ago"), falling back to an absolute date past a week. */
function relativeTime(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = then - Date.now();
  const abs = Math.abs(diffMs);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (abs < hour) return rtf.format(Math.round(diffMs / minute), 'minute');
  if (abs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
  if (abs < 7 * day) return rtf.format(Math.round(diffMs / day), 'day');
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(
    new Date(iso),
  );
}

function NotificationItem({
  n,
  onRead,
}: {
  n: AppNotification;
  onRead: (id: string) => void;
}): ReactNode {
  const { t, locale } = useT();
  const [expanded, setExpanded] = useState(false);
  const isLong = (n.body?.length ?? 0) > 120;

  return (
    <div
      className={[
        'border-b border-[var(--border-subtle)] px-4 py-3 last:border-b-0',
        n.read ? '' : 'bg-[var(--empty-surface)]',
      ].join(' ')}
    >
      <button type="button" onClick={() => onRead(n.id)} className="block w-full text-start">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium text-[var(--text-primary)]">{n.title}</span>
          <time className="mt-0.5 shrink-0 text-[11px] text-[var(--text-muted)]">
            {relativeTime(n.createdAt, locale)}
          </time>
        </div>
        <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-[var(--text-muted)]">
          {t('notifications.from')}: {sourceLabel(n.type)}
        </p>
        {n.body ? (
          <p
            className={`mt-1 text-xs text-[var(--text-secondary)] ${
              expanded ? '' : 'line-clamp-2'
            }`}
          >
            {n.body}
          </p>
        ) : null}
      </button>
      {n.body && isLong ? (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-1 text-[11px] font-semibold text-[var(--text-secondary)] underline decoration-secondary-fixed underline-offset-2 transition-colors hover:text-[var(--text-primary)]"
        >
          {expanded ? t('notifications.seeLess') : t('notifications.seeMore')}
        </button>
      ) : null}
    </div>
  );
}

/** Top-bar notifications bell: unread badge + full-height slide-in drawer with All/Unread/Read tabs. */
export function NotificationsBell(): ReactNode {
  const { t } = useT();
  const { items, unread, filter, setFilter, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-label={t('notifications.label')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-[var(--text-primary)] transition-colors hover:bg-[var(--surface-container)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60"
      >
        <Icon name="bell" size={18} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-secondary-fixed px-1 text-[10px] font-bold text-on-secondary-fixed">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Portal to <body>: the top bar has `backdrop-blur`, which would otherwise
          become the containing block for this fixed overlay and trap it in the
          64px header. Rendering at the body lets it cover the full viewport. */}
      {open &&
        createPortal(
          <div className="fixed inset-0 z-[100]" role="dialog" aria-label={t('notifications.title')}>
            {/* Backdrop — click outside the panel closes the drawer. */}
            <button
              type="button"
              aria-label={t('appShell.drawer.close')}
              onClick={() => setOpen(false)}
              className="absolute inset-0 bg-[var(--brand-ink)]/50"
            />
            <aside className="absolute inset-y-0 end-0 flex h-full w-[min(92vw,22rem)] flex-col border-s border-[var(--border-subtle)] bg-[var(--surface-raised)] shadow-panel">
              <header className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 py-3">
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  {t('notifications.title')}
                </span>
                <div className="flex items-center gap-2">
                  {unread > 0 && (
                    <button
                      type="button"
                      onClick={() => void markAllRead()}
                      className="text-xs font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
                    >
                      {t('notifications.markAll')}
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label={t('appShell.drawer.close')}
                    onClick={() => setOpen(false)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-container)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60"
                  >
                    <Icon name="close" size={16} />
                  </button>
                </div>
              </header>

              {/* All / Unread / Read tabs — each backed by its own server query. */}
              <div
                role="tablist"
                aria-label={t('notifications.title')}
                className="flex gap-1 border-b border-[var(--border-subtle)] px-2 py-2"
              >
                {TABS.map(({ key, labelKey }) => {
                  const active = filter === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFilter(key)}
                      className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                        active
                          ? 'bg-[var(--surface-container-high)] text-[var(--text-primary)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--surface-container)]'
                      }`}
                    >
                      {t(labelKey)}
                      {key === 'unread' && unread > 0 ? ` (${unread})` : ''}
                    </button>
                  );
                })}
              </div>

              <div className="flex-1 overflow-y-auto">
                {items.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-[var(--text-secondary)]">
                    {t('notifications.empty')}
                  </p>
                ) : (
                  items.map((n) => (
                    <NotificationItem key={n.id} n={n} onRead={(id) => void markRead(id)} />
                  ))
                )}
              </div>
            </aside>
          </div>,
          document.body,
        )}
    </>
  );
}
